/**
 * Topic-test launcher for a learn.4klabs.ai module.
 *
 * Renders a launch card for each test attached to the module and opens the
 * runner (public/assessment-runner.html) full-screen by share token:
 *  - the legacy bundled spec test (GET /api/learn/:token/assessment), keyed by
 *    the video's own share token; and
 *  - any new-framework interactive topic tests attached via linkedVideoToken
 *    (GET /api/learn/:token/assessments), each keyed by its own share token.
 *
 * The runner is served same-origin, so its `assessment_answered` analytics POSTs
 * come from an allowed origin and are captured per share token.
 */
import { useEffect, useState } from 'react';
import { GraduationCap, X, ArrowRight, Loader2 } from 'lucide-react';
import { fetchAssessment, fetchLinkedAssessments, type LinkedAssessment } from '../../services/learnAPI';
import { fetchAssessmentLaunchTicket } from '../../services/learnerAPI';

function TestCard({
  title,
  blurb,
  token,
  videoToken,
}: {
  title: string;
  blurb: string;
  token: string;
  videoToken: string;
}) {
  const [open, setOpen] = useState(false);
  // Signed identity for the run, minted on open. Attempts are scores, so an
  // unattributed or forgeable run is worse than a slightly slower launch.
  const [runnerUrl, setRunnerUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      setRunnerUrl(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    fetchAssessmentLaunchTicket(videoToken, token)
      .then(({ actor, sig, runUrl }) => {
        if (cancelled) return;
        // The server decides the surface: it alone can probe whether a baked CDN
        // bundle exists, and it returns the exact URL it verified. Composing one
        // here would be a guess about a host this page cannot check.
        setRunnerUrl(
          runUrl ??
            `/assessment-runner.html?token=${encodeURIComponent(token)}` +
              `&actor=${encodeURIComponent(actor)}&sig=${encodeURIComponent(sig)}`
        );
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token, videoToken]);

  return (
    <>
      <div className="rounded-2xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/[0.08] to-transparent p-5 dark:border-[#7C3AED]/40">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#7C3AED] text-white">
            <GraduationCap className="h-5 w-5" />
          </span>
          <h3 className="font-serif text-[16px] font-semibold">{title}</h3>
        </div>
        <p className="mt-2.5 text-[13px] leading-relaxed text-[#56505F] dark:text-[#B2B0C0]">{blurb}</p>
        <button
          onClick={() => setOpen(true)}
          className="mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#7C3AED] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#6D28D9]"
        >
          Take the topic test <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[200] bg-[#070A1C]">
          <button
            onClick={() => setOpen(false)}
            aria-label="Close topic test"
            className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          {runnerUrl ? (
            <iframe src={runnerUrl} title={title} className="h-full w-full border-0" allow="autoplay" />
          ) : (
            <div className="grid h-full w-full place-items-center text-white/70">
              {failed ? (
                <p className="px-6 text-center text-sm">Could not start the test. Close this and try again.</p>
              ) : (
                <Loader2 className="h-6 w-6 animate-spin" />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function AssessmentLauncher({ shareToken }: { shareToken: string }) {
  const [staticCount, setStaticCount] = useState<number | null>(null);
  const [linked, setLinked] = useState<LinkedAssessment[]>([]);

  useEffect(() => {
    let alive = true;
    fetchAssessment(shareToken).then((spec) => {
      if (alive && spec && Array.isArray(spec.questions)) setStaticCount(spec.questions.length);
    });
    fetchLinkedAssessments(shareToken).then((items) => {
      if (alive) setLinked(items);
    });
    return () => {
      alive = false;
    };
  }, [shareToken]);

  if (staticCount == null && linked.length === 0) return null; // nothing attached (or still loading)

  return (
    <div className="space-y-3">
      {staticCount != null && (
        <TestCard
          title="Topic test"
          blurb={`Check what you really understood — ${staticCount} quick questions with visuals and instant feedback.`}
          token={shareToken}
          videoToken={shareToken}
        />
      )}
      {linked.map((a) => (
        <TestCard
          key={a.shareToken}
          title={a.title ?? 'Interactive topic test'}
          blurb={`${a.questionCount} interactive questions — plot, drag, and solve to check what you really understood.`}
          token={a.shareToken}
          videoToken={shareToken}
        />
      ))}
    </div>
  );
}
